// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Company owned assets page component.
 * Displays assets that are owned by the company.
 */
import { AssetSortProperty } from '@/asset/api/get-assets';
import { AssetTable } from '@/asset/components/asset-table';
import { AssetTableSkeleton } from '@/asset/components/asset-table-skeleton';
import {
  AssetSearchListing,
  assetSearchListingSchema,
} from '@/asset/hooks/use-asset-filters';
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { AssetTableColumn } from '@/constants/asset-table-column';
import { getCustomer } from '@/customer/api/get-customer';
import { CustomerErrorBoundary } from '@/customer/components/customer-error-boundary';
import { useSort } from '@/hooks/use-sort';
import { Stack, Typography } from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Suspense } from 'react';

const OWNERSHIP_TABLE_COLUMNS: AssetTableColumn[] = [
  'serial',
  'product',
  'location',
  'activity',
];

export const Route = createFileRoute(
  '/_app/companies/$companyKey/owned-assets'
)({
  component: CompanyOwnedAssetsRoute,
  validateSearch: assetSearchListingSchema,
});

function CompanyOwnedAssetsRoute() {
  const { companyKey } = Route.useParams();
  const searchParameters = Route.useSearch();
  const navigate = Route.useNavigate();

  const handleChangePage = (page: number) => {
    navigate({ search: { ...searchParameters, page } });
  };

  return (
    <CustomerErrorBoundary>
      <CompanyOwnedAssetsContent
        companyKey={companyKey}
        assetSearchParams={searchParameters}
        handlePageChange={handleChangePage}
      />
    </CustomerErrorBoundary>
  );
}

interface CompanyOwnedAssetsContentProps {
  companyKey: string;
  assetSearchParams?: AssetSearchListing;
  handlePageChange?: (page: number) => void;
}

function CompanyOwnedAssetsContent(props: CompanyOwnedAssetsContentProps) {
  const ownershipTableSort = useSort<AssetSortProperty>('serial');

  const { data } = useSuspenseQuery({
    queryKey: ['customer', props.companyKey],
    queryFn: () => getCustomer(props.companyKey),
  });

  return (
    <HardfinTabCardWrapper title="Assets owned">
      <Stack paddingTop={2}>
        <Suspense
          fallback={
            <AssetTableSkeleton columns={OWNERSHIP_TABLE_COLUMNS.length} />
          }
        >
          <AssetTable
            sort={ownershipTableSort.sort}
            onSort={ownershipTableSort.setSort}
            filter={{
              customerIds: [data.customer.id],
              pageSize: 50,
              pageNumber: props.assetSearchParams?.page ?? 1,
            }}
            columns={OWNERSHIP_TABLE_COLUMNS}
            onPageChange={props.handlePageChange}
            emptyState={
              <Typography variant="body2" color="text.secondary">
                This company does not own any assets
              </Typography>
            }
          />
        </Suspense>
      </Stack>
    </HardfinTabCardWrapper>
  );
}
